// src/components/BoxesPage.tsx
import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import DynamicBoxes from "./DynamicBoxes";
import { BoxType } from "../utils/boxTypes";
import BoxesPageHeading from "./BoxesPageHeading";
import Footer from "./Footer";
import localization from "../services/localization";
import HeroComponent from "./HeroComponent";
import { AppDispatch, RootState } from "../app/store";
import { useDispatch, useSelector } from "react-redux";
import HowItWorksPage from "./HowItWorks";
import { useNavigate } from "react-router-dom";
import { Platform, PromoSlider } from "keycloakauthmodule";
import { handlePageSelection } from "../features/pageSlice";

const BoxesPageWrapper = styled.div`
  padding: 20px;
  color: #fff;
`;

const Spacer = styled.div`
  height: 100px;
`;

const Spacer10 = styled.div`
  height: 10px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`;

interface ScrollButtonProps {
  $gradientColors: string[];
}

const ScrollButton = styled.button<ScrollButtonProps>`
  font-family: "BernierShade", sans-serif;
  font-size: 18px;
  font-weight: normal;
  letter-spacing: normal;
  text-align: center;
  padding: 12px 24px;
  border: none;
  cursor: pointer;
  border-radius: 5px;
  background: linear-gradient(to bottom, ${(props) => props.$gradientColors.join(", ")});
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  border: 2px solid ${(props) => props.$gradientColors[0]};
  transition: all 0.3s ease-in-out;

  &:hover {
    background: linear-gradient(to bottom, ${(props) => [...props.$gradientColors].reverse().join(", ")});
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    border-color: ${(props) => props.$gradientColors[0]};
  }

  @media (max-width: 768px) {
    font-size: 16px;
    padding: 10px 20px;
  }
`;

const WidgetSection = styled.div`
  width: 100%;
  margin-top: 16px;
`;

const WidgetFrame = styled.iframe`
  width: 100%;
  height: 900px;
  border: 0;
  display: block;
  border-radius: 12px;
  overflow: auto;
`;

const BoxesPage: React.FC = () => {
  const [boxCount, setBoxCount] = useState(2);
  const contentRef = useRef<HTMLDivElement>(null);
  const howItWorksRef = useRef<HTMLDivElement>(null);

  const featuredBoxes = useSelector((state: RootState) => state.boxes.featuredBoxes);
  const vpcBoxes = useSelector((state: RootState) => state.boxes.vpcBoxes);
  const user = useSelector((state: RootState) => state.authSlice.user);

  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();

  const TOKEN = localStorage.getItem("token");

  useEffect(() => {
    const handleResize = () => {
      const windowWidth = window.innerWidth;
      if (windowWidth < 600) {
        setBoxCount(2);
      } else if (windowWidth < 768) {
        setBoxCount(3);
      } else if (windowWidth < 1024) {
        setBoxCount(4);
      } else {
        setBoxCount(6);
      }
    };

    const handleContentHeight = () => {
      if (contentRef.current) {
        const contentHeight = contentRef.current.scrollHeight;
        const windowHeight = window.innerHeight;
        const newMaxHeight = Math.min(contentHeight, windowHeight);
        contentRef.current.style.maxHeight = `${newMaxHeight}px`;
      }
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("resize", handleContentHeight);

    handleResize();
    handleContentHeight();

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("resize", handleContentHeight);
    };
  }, []);

  useEffect(() => {
    if (!TOKEN) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === "REQUEST_JWT") {
        const token = localStorage.getItem("token");

        if (event.source && token) {

          const payload = {
            type: "DELIVER_JWT",
            token: token,
            email: user?.email || "",
            username: user?.username || ""
          };

          // ✅ Print what we are sending
          console.log("Parent sending payload to iframe:", payload);

          (event.source as Window).postMessage(payload, event.origin);
        }
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [TOKEN, user]);

  const scrollToHowItWorks = () => {
    if (howItWorksRef.current) {
      howItWorksRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <BoxesPageWrapper ref={contentRef}>
      <HeroComponent />

      {TOKEN && (
        <WidgetSection>
          <WidgetFrame
            src="https://omegajam.com/widget/Academy"
            title="OmegaJam Academy Widget"
            allow="fullscreen; clipboard-read; clipboard-write"
          />
        </WidgetSection>
      )}

      <ButtonContainer>
        <ScrollButton onClick={scrollToHowItWorks} $gradientColors={["#ffd400", "#0af"]}>
          How It Works
        </ScrollButton>
      </ButtonContainer>

      <Spacer10 />

      <PromoSlider
        platform={Platform.BOXCHAOS}
        autoplay
        interval={5000}
        onNavigate={(to) => {
          if (to.startsWith("/promotions")) {
            dispatch(handlePageSelection("promotions"));
          }
          navigate(to);
        }}
      />

      <>
        {featuredBoxes.length > 0 && (
          <BoxesPageHeading $gradientColors={["#ffd400 0%", "#0af 100%"]}>
            {localization.featured_boxes}
          </BoxesPageHeading>
        )}
        <DynamicBoxes boxCount={boxCount} boxType={BoxType.Featured} />
      </>

      <>
        {vpcBoxes.length > 0 && (
          <BoxesPageHeading $gradientColors={["#ffd400 0%", "#0af 100%"]}>
            {localization.vpc_boxes}
          </BoxesPageHeading>
        )}
        <DynamicBoxes boxCount={boxCount} boxType={BoxType.Vpc} />
      </>

      <BoxesPageHeading $gradientColors={["#f00 0%", "#ff0 100%"]}>
        {localization.hot_boxes}
      </BoxesPageHeading>
      <DynamicBoxes boxCount={boxCount} boxType={BoxType.Hot} />

      <BoxesPageHeading $gradientColors={["#0af 0%", "#ff0 100%"]}>
        {localization.new_boxes}
      </BoxesPageHeading>
      <DynamicBoxes boxCount={boxCount} boxType={BoxType.New} />

      <div ref={howItWorksRef}>
        <HowItWorksPage isNeedToHideFooter={true} />
      </div>

      <Footer />
      <Spacer />
    </BoxesPageWrapper>
  );
};

export default BoxesPage;
